import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DataService } from '../../../shared/services/data.service';
import { IOTDeviceDetails } from '../models/device.model';
import { DeviceSummary } from '../models/deviceSummary.model';
import { EdgeDeviceSearchDto } from '../models/edgeDeviceSearchDto.model';
import { EdgeDeviceSearchOptions } from '../models/edgeDeviceSearchOptions.model';

@Injectable({
  providedIn: 'root',
})
export class FleetManagementService extends DataService<IOTDeviceDetails> {

  httpClient: HttpClient;

  cdcModuleId = 'sql-cdc-module';

  constructor(http: HttpClient) {
    super(`${environment.apiUrl.iotDeviceMgmtService}/`, http);
    this.httpClient = http;
  }

  getRestartCDCModule(deviceID): Observable<any> {
    return this.http.get<any[]>(`${this.url}${deviceID}/Modules/${this.cdcModuleId}/Restart`);
  }

  getRestartModules(deviceID): Observable<any> {
    return this.http.get<any[]>(`${this.url}${deviceID}/Modules/Restart`);
  }

  getRestartDevice(deviceID): Observable<any> {
    return this.http.get<any[]>(`${this.url}${deviceID}/Restart`);
  }

  getProvisionedSystems(): Observable<any> {
    return this.http.get<any>(`${this.url}systems/provisioned`);
  }

  getDeviceInventory(): Observable<IOTDeviceDetails> {
    return this.http.get<any>(`${this.url}`);
  }

  getDeviceLog(deviceID, logOptions): Observable<any> {
    return this.http.post<any>(`${this.url}${deviceID}/Modules/logs`,logOptions, {responseType: 'blob' as 'json'});
  }

  getDeviceInformation(deviceID): Observable<any> {
    return this.http.post<any>(`${this.url}${deviceID}/Device`, {responseType: 'blob' as 'json'});
  }

  deprovisionDevice(deviceId) {
    return this.http.delete<any>(`${this.url}${deviceId}`);
  }
  
  getSystemssByEdgeDeviceId(edgeDeviceId): Observable<any> {
    return this.http.get<any>(`${this.url}${edgeDeviceId}`);
  }

  getSystemsInSiteByIp(siteId: string, ipAddress: string): Observable<any> {
    return this.http.get<any>(`${this.url}systems/${siteId}/${ipAddress}`)
  }

  getEdgeDeviceById(edgeDeviceId: string) {
    return this.http.get<any>(`${this.url}${edgeDeviceId}`);
  }

  getEdgeDeviceSummary(edgeDeviceId: string): Observable<DeviceSummary> {
    return this.http.get<any>(`${this.url}${edgeDeviceId}/summary`);
  }

  executeSystemAction(edgeDeviceId: string,systemId: string, actionName: string, body?: any): Observable<{ success: boolean; message: string }> {
    const url = `${this.url}${edgeDeviceId}/systems/${systemId}/actions/${actionName}`;
    return this.http.post<{ success: boolean; message: string }>(url, body ?? {});
  }

  searchEdgeDevices(searchParams: EdgeDeviceSearchOptions): Observable<EdgeDeviceSearchDto[]> {
    let params = new HttpParams();
    if (searchParams.orgId) {
      params = params.set('organizationId', searchParams.orgId);
    }
    if (searchParams.provisioned) {
      params = params.set('provisioned', searchParams.provisioned.toString());
    }
    if (searchParams.enabled) {
      params = params.set('enabled', searchParams.enabled.toString());
    }
    return this.http.get<EdgeDeviceSearchDto[]>(`${this.url}search`, { params });
  }

  executeSystemActionOperation(edgeDeviceId: string, systemId: string, actionName: string, operation: string, body?: any): Observable<{ success: boolean; message: string; data?: any }> {
    const url = `${this.url}${edgeDeviceId}/systems/${systemId}/actions/${actionName}/operations/${operation}`;
    return this.http.post<{ success: boolean; message: string; data?: any }>(url, body ?? {});
  }

  executeDeploymentAction(edgeDeviceId: string, actionName: string, body?: any): Observable<{ success: boolean; message: string; data?: any }> {
    const url = `${this.url}${edgeDeviceId}/actions/${actionName}`;
    return this.http.post<{ success: boolean; message: string; data?: any }>(url, body ?? {});
  }

  executeDeploymentActionOperation(edgeDeviceId: string, actionName: string, operation: string, body?: any): Observable<{ success: boolean; message: string; data?: any }> {
    const url = `${this.url}${edgeDeviceId}/actions/${actionName}/operations/${operation}`;
    return this.http.post<{ success: boolean; message: string; data?: any }>(url, body ?? {});
  }

  updateOsDistro(deviceId: string, osDistro: string): Observable<{ deviceId: string; osDistro: string }> {
    return this.http.patch<{ deviceId: string; osDistro: string }>(`${this.url}${deviceId}/os-distro`, { osDistro });
  }

  getOsDistros(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}os-distros`);
  }
}
